import {
  EffectiveDateProperties,
  EntityDetails,
  EntityRelationship,
  EntityTypeMetadata,
  EditableRowField,
  EditableRow,
  ValidationMessage,
  RelationshipType,
  getDetailsFieldLabel,
} from './entity-metadata'
import { orderBy } from 'lodash'
import { Option } from '../shared/models/general'
import { type NewEntity } from './new-entity.component'

export type ChildEntityOption = Option & Readonly<{
  termination_date?: string
}>

type RowGroup<T> = Readonly<{ rows: readonly EditableRow<T>[] }>

const compareDates = (left: string, right: string) => {
  const leftAsIsoDate = left.slice(0, 10)
  const rightAsIsoDate = right.slice(0, 10)
  if (leftAsIsoDate < rightAsIsoDate) {
    return -1
  } else if (leftAsIsoDate > rightAsIsoDate) {
    return 1
  } else {
    return 0
  }
}


const getGroups = <T> (
  rows: readonly EditableRow<T>[],
  groupBy?: EditableRowField<T>,
): readonly RowGroup<T>[] => {
  if (groupBy) {
    const rowGroups: Record<string, EditableRow<T>[]> = {}

    const add = (row: EditableRow<T>) => {
      const groupId = String(row[groupBy])
      let group = rowGroups[groupId]
      if (!group) {
        group = []
        rowGroups[groupId] = group
      }
      group.push(row)
    }

    rows.forEach(add)

    return Object.values(rowGroups).map(rows => ({ rows }))
  } else {
    return [{ rows }]
  }
}

const requiredMessage = (id: string, field?: string): ValidationMessage => ({
  text: 'Value is required',
  targets: [{ id, ...field ? { field } : {} }],
})

const validate = <T extends EffectiveDateProperties> (
  rows: readonly EditableRow<T>[],
  getId: (row: EditableRow<T>) => string,
  required: readonly EditableRowField<T>[],
  groupBy?: EditableRowField<T>,
) => {
  const label = (field: string) => getDetailsFieldLabel(field)

  const validateRequiredValues = (row: EditableRow<T>) => {
    const messages: ValidationMessage[] = []

    const addRequired = (field: EditableRowField<T>) => {
      messages.push(requiredMessage(getId(row), field))
    }

    required.forEach(field => {
      if (row[field] === undefined) {
        addRequired(field)
      }
    })
    return messages
  }

  const validateEffectiveDateWindow = (
    row: EditableRow<T>,
  ): readonly ValidationMessage[] => {
    if (row.effective_date && row.termination_date && compareDates(row.termination_date, row.effective_date) < 0) {
      return [{
        text: `Must be greater than or equal to ${label('effective_date')}`,
        targets: [{ id: getId(row), field: 'termination_date' }],
      }]
    } else {
      return []
    }
  }

  const result: ValidationMessage[] = []

  const validateRow = (row: EditableRow<T>) => {
    if (row.new || row.original) {
      result.push(...validateRequiredValues(row))
    }
    result.push(...validateEffectiveDateWindow(row))
  }


  const validateDateRanges = (
    group: readonly EditableRow<T>[],
  ) => {
    const getDateValue = (
      row: EditableRow<T>,
      field: keyof EffectiveDateProperties,
    ) => (row[field] ?? '').slice(0, 10)

    const result: ValidationMessage[] = []
    // get all members of the group with an effective date and make sure they don't overlap any other date windows
    const sortedGroup = orderBy(group.filter(_ => _.effective_date), 'effective_date', 'desc')
    if (sortedGroup.length > 1) {
      const effectiveDates = new Set<string>()
      let effectiveDate = getDateValue(sortedGroup[0], 'effective_date')
      let id = getId(sortedGroup[0])
      sortedGroup.slice(1).forEach(row => {
        effectiveDates.add(effectiveDate)
        const nextEffectiveDate = getDateValue(row, 'effective_date')
        const nextId = getId(row)
        if (effectiveDates.has(nextEffectiveDate)) {
          result.push({
            text: `Duplicate value: ${nextEffectiveDate}`,
            targets: [id, nextId].map(_ => ({ id: _, field: 'effective_date' })),
          })
        } else {
          const terminationDate = getDateValue(row, 'termination_date')
          if (compareDates(terminationDate, effectiveDate) >= 0) {
            result.push({
              text: `${label('effective_date')} ${effectiveDate} must be after ${label('termination_date')} ${terminationDate}`,
              targets: [
                { id, field: 'effective_date' },
                { id: nextId, field: 'termination_date' },
              ],
            })
          }
        }
        effectiveDate = nextEffectiveDate
        id = nextId
      })
    }
    return result
  }

  rows.forEach(validateRow)

  getGroups(rows, groupBy).forEach(group => {
    result.push(...validateDateRanges(group.rows))
  })

  return result
}

const validateAgainstChildEntityOptionTerminationDate = (
  rows: readonly EditableRow<EntityRelationship>[],
  getId: (row: EditableRow<EntityRelationship>) => string,
  relatedEntityOptionLookup: Readonly<Record<string, Option>>,
): readonly ValidationMessage[] => {
  const messages: ValidationMessage[] = []
  rows.filter(_ => _.effective_date).forEach(row => {
    const option = relatedEntityOptionLookup[row.child_entity_uid!] as ChildEntityOption | undefined
    const terminationDate = option?.termination_date
    if (terminationDate && compareDates(row.effective_date!, terminationDate) <= 0) {
      messages.push({
        text: `Must be after than existing relationship ${getDetailsFieldLabel('termination_date')}`,
        targets: [{ id: getId(row), field: 'effective_date' }],
      })
    }
  })
  return messages
}

export const validateEntityDetails = (
  metadata: EntityTypeMetadata,
  getId: (row: EditableRow<EntityDetails>) => string,
  rows: readonly EditableRow<EntityDetails>[],
): readonly ValidationMessage[] =>
  validate(rows, getId, metadata.required)

export const validateRelationships = (
  rows: readonly EditableRow<EntityRelationship>[],
  getId: (row: EditableRow<EntityRelationship>) => string,
  relationshipType: RelationshipType,
  relatedEntityOptionLookup: Readonly<Record<string, Option>>,
): readonly ValidationMessage[] => {
  const groupBy = relationshipType === 'parent' ? undefined : 'child_entity_uid'
  const required: readonly EditableRowField<EntityRelationship>[] = [
    relationshipType === 'parent' ? 'parent_entity_uid' : 'child_entity_uid',
    'effective_date',
    'termination_date',
  ]
  const messages =  validate(rows, getId, required, groupBy)
  if (relationshipType === 'child') {
    return [...messages, ...validateAgainstChildEntityOptionTerminationDate(rows, getId, relatedEntityOptionLookup)]
  } else {
    return messages
  }
}

export const validateNewEntity = (
  newEntity: NewEntity,
  entityTypeMetadata: EntityTypeMetadata,
  existingCodes: ReadonlySet<string>,
): readonly ValidationMessage[] => {
  const messages: ValidationMessage[] = []

  const requiredFields = ['entity_code', 'entity_name', ...entityTypeMetadata.required]

  requiredFields.forEach(field => {
    if (newEntity[field as keyof NewEntity] === undefined) {
      messages.push(requiredMessage(field))
    }
  })

  if (newEntity.entity_code && existingCodes.has(newEntity.entity_code)) {
    messages.push({ text: `${entityTypeMetadata.entity_type_name} '${newEntity.entity_code}' already exists`, targets: [{ id: 'entity_code' }] })
  }

  return messages
}
